import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from '@hig/theme-context';
import { css, cx } from 'emotion';

var SIZES = Object.freeze({
  s: "small",
  m: "medium",
  l: "large"
});

var AVAILABLE_BORDER_RADII = Object.freeze(Object.keys(SIZES));
var AVAILABLE_LEVELS = Object.freeze([100, 200, 250, 300, 350]);
var AVAILABLE_SHADOWS = Object.freeze(["high", "low"]);
var AVAILABLE_SPACINGS = Object.freeze(Object.keys(SIZES));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function stylesheet(props, themeData) {
  function sizeKey(size) {
    return size ? SIZES[size] : "none";
  }

  function spacing(size) {
    return size === "none" ? "basics.spacings.none" : "density.spacings." + size;
  }

  var borderRadius = props.borderRadius,
      horizontalPadding = props.horizontalPadding,
      verticalPadding = props.verticalPadding,
      level = props.level,
      shadow = props.shadow,
      customStylesheet = props.stylesheet;

  var styles = {
    surface: _extends({
      backgroundColor: themeData["colorScheme.surface.level" + level],
      padding: themeData[spacing(sizeKey(verticalPadding))] + " " + themeData[spacing(sizeKey(horizontalPadding))],
      borderRadius: themeData["basics.borderRadii." + sizeKey(borderRadius)]

    }, shadow && {
      boxShadow: "0 0 " + themeData["basics.shadows." + shadow + "Blur"] + " 0 " + themeData["colorScheme.shadow." + shadow]
    })
  };

  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var Surface = function (_Component) {
  _inherits(Surface, _Component);

  function Surface() {
    _classCallCheck(this, Surface);

    return _possibleConstructorReturn(this, (Surface.__proto__ || Object.getPrototypeOf(Surface)).apply(this, arguments));
  }

  _createClass(Surface, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          borderRadius = _props.borderRadius,
          children = _props.children,
          horizontalPadding = _props.horizontalPadding,
          verticalPadding = _props.verticalPadding,
          level = _props.level,
          shadow = _props.shadow,
          customStylesheet = _props.stylesheet,
          tagName = _props.tagName,
          otherProps = _objectWithoutProperties(_props, ["borderRadius", "children", "horizontalPadding", "verticalPadding", "level", "shadow", "stylesheet", "tagName"]);

      var className = otherProps.className;

      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var resolvedRoles = _ref.resolvedRoles;

          var styles = stylesheet({
            borderRadius: borderRadius,
            stylesheet: customStylesheet,
            horizontalPadding: horizontalPadding,
            verticalPadding: verticalPadding,
            level: level,
            shadow: shadow
          }, resolvedRoles);

          var Element = tagName;

          return React.createElement(
            Element,
            { className: cx(css(styles.surface), className) },
            children
          );
        }
      );
    }
  }]);

  return Surface;
}(Component);

Surface.propTypes = {
  /**
   * Specifies the the radius of the border. The default is no radius.
   */
  borderRadius: PropTypes.oneOf(AVAILABLE_BORDER_RADII),
  /**
   * Children to be rendered within the component.
   */
  children: PropTypes.node,
  /**
   * Specifies the amount of padding to be applied horizontally to the element. The default is no padding.
   */
  horizontalPadding: PropTypes.oneOf(AVAILABLE_SPACINGS),
  /**
   * Specifies the amount of padding to be applied vertically to the element. The default is no padding.
   */
  verticalPadding: PropTypes.oneOf(AVAILABLE_SPACINGS),
  /**
   * Specifies the color level of the background color for the element, respective of theme. The default is 100.
   */
  level: PropTypes.oneOf(AVAILABLE_LEVELS),
  /**
   * Specifies the level of shadow to surround the element.
   */
  shadow: PropTypes.oneOf(AVAILABLE_SHADOWS),
  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func,
  /**
   * Enables specifying the semantic element to be rendered by the component
   * The default is "div"
   */
  tagName: PropTypes.string
};
Surface.defaultProps = {
  level: 100,
  tagName: "div"
};
Surface.__docgenInfo = {
  "description": "",
  "displayName": "Surface",
  "props": {
    "borderRadius": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_BORDER_RADII"
      },
      "required": false,
      "description": "Specifies the the radius of the border. The default is no radius."
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Children to be rendered within the component."
    },
    "horizontalPadding": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_SPACINGS"
      },
      "required": false,
      "description": "Specifies the amount of padding to be applied horizontally to the element. The default is no padding."
    },
    "verticalPadding": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_SPACINGS"
      },
      "required": false,
      "description": "Specifies the amount of padding to be applied vertically to the element. The default is no padding."
    },
    "level": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_LEVELS"
      },
      "required": false,
      "description": "Specifies the color level of the background color for the element, respective of theme. The default is 100.",
      "defaultValue": {
        "value": "100",
        "computed": false
      }
    },
    "shadow": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_SHADOWS"
      },
      "required": false,
      "description": "Specifies the level of shadow to surround the element."
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    },
    "tagName": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Enables specifying the semantic element to be rendered by the component\nThe default is \"div\"",
      "defaultValue": {
        "value": "\"div\"",
        "computed": false
      }
    }
  }
};

export default Surface;
export { AVAILABLE_BORDER_RADII, AVAILABLE_LEVELS, AVAILABLE_SHADOWS, AVAILABLE_SPACINGS };
